import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { API_BASE_URL, API_CONFIG } from '../../constants/api';
import { CrudOperations, QueryParams, ApiError } from '../../types/common';

/**
 * Base service class for API operations
 */
export abstract class BaseService<T, C, U> implements CrudOperations<T, C, U> {
  protected api: AxiosInstance;
  protected endpoint: string;

  constructor(endpoint: string) {
    this.endpoint = endpoint;
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: API_CONFIG.TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor
    this.api.interceptors.request.use(
      (config) => {
        // Add auth token if available
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Handle 401 errors
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          
          // Try to refresh token
          try {
            const refreshToken = localStorage.getItem('refresh_token');
            if (refreshToken) {
              const refreshResponse = await axios.post(`${API_BASE_URL}/auth/refresh`, {
                refresh_token: refreshToken
              });
              
              if (refreshResponse.data.access_token) {
                // Store new tokens
                localStorage.setItem('access_token', refreshResponse.data.access_token);
                if (refreshResponse.data.refresh_token) {
                  localStorage.setItem('refresh_token', refreshResponse.data.refresh_token);
                }
                
                // Retry original request with new token
                originalRequest.headers.Authorization = `Bearer ${refreshResponse.data.access_token}`;
                return this.api(originalRequest);
              }
            }
          } catch (refreshError) {
            // Refresh failed, redirect to login
            console.error('Token refresh failed:', refreshError);
          }
          
          // If refresh fails or no refresh token, redirect to login
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user_email');
          window.location.href = '/login';
        }

        return Promise.reject(this.transformError(error));
      }
    );
  }

  /**
   * Transform axios error to ApiError
   */
  protected transformError(error: any): ApiError {
    if (error.response) {
      // Server responded with error
      return {
        message: error.response.data?.detail || error.response.data?.message || 'Server error',
        details: error.response.data,
        code: error.response.status.toString(),
      };
    } else if (error.request) {
      // Request was made but no response
      return {
        message: 'Network error. Please check your connection.',
        code: 'NETWORK_ERROR',
      };
    } else {
      // Something else happened
      return {
        message: error.message || 'An unexpected error occurred',
        code: 'UNKNOWN_ERROR',
      };
    }
  }

  /**
   * Build query string from params
   */
  protected buildQueryString(params?: QueryParams): string {
    if (!params) return '';
    
    const query = new URLSearchParams();
    
    if (params.page !== undefined) query.append('skip', ((params.page - 1) * (params.pageSize || 10)).toString());
    if (params.pageSize !== undefined) query.append('limit', params.pageSize.toString());
    if (params.search) query.append('search', params.search);
    if (params.sortBy) query.append('sort_by', params.sortBy);
    if (params.sortOrder) query.append('sort_order', params.sortOrder);
    
    // Add filters
    if (params.filters) {
      Object.entries(params.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          query.append(key, value.toString());
        }
      });
    }
    
    const queryString = query.toString();
    return queryString ? `?${queryString}` : '';
  }

  /**
   * List all entities
   */
  async list(params?: QueryParams): Promise<T[]> {
    const response = await this.api.get<T[]>(`${this.endpoint}${this.buildQueryString(params)}`);
    return response.data;
  }

  /**
   * Get entity by ID
   */
  async get(id: number): Promise<T> {
    const response = await this.api.get<T>(`${this.endpoint}/${id}`);
    return response.data;
  }

  /**
   * Create new entity
   */
  async create(data: C): Promise<T> {
    const response = await this.api.post<T>(this.endpoint, data);
    return response.data;
  }

  /**
   * Update existing entity
   */
  async update(id: number, data: U): Promise<T> {
    const response = await this.api.put<T>(`${this.endpoint}/${id}`, data);
    return response.data;
  }

  /**
   * Delete entity
   */
  async delete(id: number): Promise<void> {
    await this.api.delete(`${this.endpoint}/${id}`);
  }

  /**
   * Make custom GET request
   */
  protected async getRequest<R>(path: string, config?: AxiosRequestConfig): Promise<R> {
    const response = await this.api.get<R>(path, config);
    return response.data;
  }

  /**
   * Make custom POST request
   */
  protected async postRequest<R, D = any>(path: string, data?: D, config?: AxiosRequestConfig): Promise<R> {
    const response = await this.api.post<R>(path, data, config);
    return response.data;
  }

  /**
   * Make custom PUT request
   */
  protected async putRequest<R, D = any>(path: string, data?: D, config?: AxiosRequestConfig): Promise<R> {
    const response = await this.api.put<R>(path, data, config);
    return response.data;
  }

  /**
   * Make custom DELETE request
   */
  protected async deleteRequest<R>(path: string, config?: AxiosRequestConfig): Promise<R> {
    const response = await this.api.delete<R>(path, config);
    return response.data;
  }
}